import { memo, useMemo } from "react";
import { Link } from "react-router-dom";

import { ContextMenu } from "@/components/filemanager/ContextMenu";
import { FavoriteStarButton } from "@/components/filemanager/FavoriteStarButton";
import type { FolderItem } from "@/lib/api";
import { folderRoute } from "@/lib/folderUpload";

interface FolderCardProps {
  folder: FolderItem;
  onRename?: (folder: FolderItem) => void;
  onDelete?: (folder: FolderItem) => void;
  onToggleFavorite?: (folder: FolderItem) => void;
}

export const FolderCard = memo(function FolderCard({
  folder,
  onRename,
  onDelete,
  onToggleFavorite,
}: FolderCardProps) {
  const menuItems = useMemo(
    () =>
      onRename || onDelete || onToggleFavorite
        ? [
            ...(onToggleFavorite
              ? [
                  {
                    label: folder.favorite ? "Remover dos favoritos" : "Favoritar",
                    onSelect: () => onToggleFavorite(folder),
                  },
                ]
              : []),
            ...(onRename ? [{ label: "Renomear" as const, onSelect: () => onRename(folder) }] : []),
            ...(onDelete
              ? [
                  {
                    label: "Excluir" as const,
                    destructive: true as const,
                    onSelect: () => onDelete(folder),
                  },
                ]
              : []),
          ]
        : [],
    [folder, onDelete, onRename, onToggleFavorite],
  );

  return (
    <div className="group relative aspect-square rounded-lg border border-ink-700 bg-ink-800/40 transition hover:border-ink-600 hover:bg-ink-800">
      <Link
        to={folderRoute(folder.path)}
        className="flex h-full flex-col items-center justify-center gap-2 rounded-lg p-3"
      >
        <svg
          className="h-16 w-16 shrink-0 text-yellow-500"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
        </svg>
        <div className="w-full truncate text-center text-sm font-medium" title={folder.name}>
          {folder.name}
        </div>
        <div className="text-xs text-ink-400">{folder.asset_count} arquivos</div>
      </Link>
      {onToggleFavorite ? (
        <div className="absolute left-1 top-1 z-10">
          <FavoriteStarButton folder={folder} onToggle={onToggleFavorite} />
        </div>
      ) : null}
      {menuItems.length > 0 ? (
        <div className="absolute right-1 top-1 z-10">
          <ContextMenu items={menuItems} ariaLabel={`Ações da pasta ${folder.name}`} />
        </div>
      ) : null}
    </div>
  );
});
